import {useState, useEffect} from 'react';
import {trpc} from '@common-web/lib/trpc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Textarea} from '@common-web/components/ui/textarea';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {useAuthStore} from '@common-web/stores/authStore';
import {Loader2, Send} from 'lucide-react';
import {toast} from '@common-web/hooks/use-toast';

export function SupportContactModal({
  isOpen,
  onClose,
  agentId,
}: {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
}) {
  const userEmail = useAuthStore((state) => state.user?.email);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  function onResetAndClose() {
    onClose();
    setEmail('');
    setMessage('');
  }

  const contactSupport = trpc.group.payrollProcessing.contactSupport.useMutation({
    onSuccess: () => {
      toast({
        title: 'Message Sent',
        description: 'We have received your message and will get back to you shortly.',
      });
      onResetAndClose();
    },
    onError: (error) => {
      toast({
        title: 'Error Sending Message',
        description: error.message || 'An error occurred while sending your message.',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (isOpen && userEmail) {
      setEmail(userEmail);
    }
  }, [isOpen, userEmail]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    contactSupport.mutate({
      agentId,
      email,
      message,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onResetAndClose()}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Contact Support</DialogTitle>
          <DialogDescription>
            This is the fastest way to reach us for issues or questions regarding payroll
            processing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='email'>Your Email</Label>
            <Input
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='your@email.com'
              required
              type='email'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='message'>How can we help?</Label>
            <Textarea
              id='message'
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder='Describe your issue or question here...'
              className='min-h-[120px]'
              required
            />
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={onResetAndClose}
              disabled={contactSupport.isPending}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={contactSupport.isPending}>
              {contactSupport.isPending ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Sending...
                </>
              ) : (
                <>
                  <Send className='mr-2 h-4 w-4' />
                  Send Message
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
